<template>
  <transition
    name="play-sidebar"
    @after-leave="onAfterLeave"
  >
    <div
      v-if="visible"
      class="play-sidebar"
      :class="[`play-sidebar--${side}`, { 'play-sidebar--shadow': shadow }]"
      @mousedown.self="onBackdrop"
    >
      <aside
        ref="panel"
        class="play-sidebar__panel"
        :style="{ '--sb-width': resolvedWidth }"
        role="dialog"
        aria-modal="true"
        :aria-label="title || null"
        tabindex="-1"
      >
        <header
          v-if="!noHeader"
          class="play-sidebar__header"
        >
          <slot name="header">
            <h2 class="play-sidebar__title">
              {{ title }}
            </h2>
            <button
              type="button"
              class="play-sidebar__close"
              :aria-label="closeLabel"
              @click="close"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </slot>
        </header>

        <div class="play-sidebar__body">
          <slot />
        </div>

        <footer
          v-if="$slots.footer"
          class="play-sidebar__footer"
        >
          <slot name="footer" />
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script>
/**
 * PlaySidebar — slide-in drawer for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-sidebar>. Driven by v-model (`visible`) instead of the global v-b-toggle
 * registry — a controlled boolean is cleaner and Vue 3-safe. Slides from the
 * `side` (left/right); handles scroll-lock, Esc/backdrop close and focus
 * restore (same machinery as PlayModal). `header`/default/`footer` slots fill
 * the regions; `title` + the close button cover the common header.
 *
 * VUE3-MIGRATION:
 *  - `model: { prop: 'visible', event: 'change' }` → Vue 3 `modelValue` /
 *    `update:modelValue`; change this block + the emit name.
 *  - Transition classes use Vue 2 names (`-enter`/`-leave`); Vue 3 renames them
 *    to `-enter-from`/`-leave-from` (see the <style> block).
 */
export default {
  name: 'PlaySidebar',
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    /** Slide-in edge */
    side: {
      type: String,
      default: 'right',
      validator: v => ['left', 'right'].includes(v),
    },
    /** Panel width: number (px) or raw CSS length */
    width: {
      type: [Number, String],
      default: 360,
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    noHeader: {
      type: Boolean,
      default: false,
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },
    closeLabel: {
      type: String,
      default: 'Close',
    },
  },
  data() {
    return {
      previouslyFocused: null,
      prevBodyOverflow: '',
    }
  },
  computed: {
    resolvedWidth() {
      return typeof this.width === 'number' ? `${this.width}px` : this.width
    },
  },
  watch: {
    visible(val) {
      if (val) this.handleOpen()
      else this.handleClose()
    },
  },
  mounted() {
    if (this.visible) this.handleOpen()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown, true)
    if (this.visible) this.unlockScroll()
  },
  methods: {
    handleOpen() {
      this.$emit('show')
      this.previouslyFocused = document.activeElement
      this.lockScroll()
      document.addEventListener('keydown', this.onKeydown, true)
      this.$nextTick(() => {
        if (this.$refs.panel) this.$refs.panel.focus()
        this.$emit('shown')
      })
    },
    handleClose() {
      this.$emit('hide')
      document.removeEventListener('keydown', this.onKeydown, true)
      this.unlockScroll()
      const el = this.previouslyFocused
      if (el && typeof el.focus === 'function') el.focus()
      this.previouslyFocused = null
    },
    onAfterLeave() {
      this.$emit('hidden')
    },
    close() {
      this.$emit('change', false)
    },
    onBackdrop() {
      if (!this.noCloseOnBackdrop) this.close()
    },
    onKeydown(e) {
      if (e.key === 'Escape' && !this.noCloseOnEsc) {
        e.preventDefault()
        this.close()
      }
    },
    lockScroll() {
      this.prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    unlockScroll() {
      document.body.style.overflow = this.prevBodyOverflow
    },
  },
}
</script>

<style lang="scss" scoped>
.play-sidebar {
  position: fixed;
  inset: 0;
  z-index: 1045;
  display: flex;
  background: rgba(27, 20, 34, .5);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);

  &--left { justify-content: flex-start; }
  &--right { justify-content: flex-end; }

  &__panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: var(--sb-width, 360px);
    height: 100%;
    background: var(--surface-card);
    border-left: var(--border-w-bold) solid var(--border-ink);

    &:focus { outline: none; }
  }

  &--left &__panel {
    border-left: 0;
    border-right: var(--border-w-bold) solid var(--border-ink);
  }

  &--shadow &__panel { box-shadow: var(--shadow-hard-lg); }

  /* ── Header ── */
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-shrink: 0;
    padding: 18px 20px;
    border-bottom: var(--border-w) solid var(--border-ink);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 19px;
    letter-spacing: -.02em;
    color: var(--text-strong);
  }

  &__close {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    padding: 0;
    background: var(--surface-bg);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-sm);
    box-shadow: 0 3px 0 var(--border-ink);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition:
      transform var(--dur-fast) var(--ease-bounce),
      box-shadow var(--dur-fast) var(--ease-out);

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: var(--text-strong);
      stroke-width: 3;
      stroke-linecap: round;
    }

    &:hover { box-shadow: 0 4px 0 var(--border-ink); }
    &:active { transform: translateY(2px); box-shadow: var(--shadow-press); }
    &:focus-visible { outline: 2px solid var(--c-secondary); outline-offset: 2px; }
  }

  /* ── Body / footer ── */
  &__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 20px;
    font-family: var(--font-body);
    color: var(--text-body);
    line-height: 1.55;
  }

  &__footer {
    flex-shrink: 0;
    padding: 16px 20px;
    border-top: var(--border-w) solid var(--border-ink);
  }
}

/* ── Transition (Vue 2 class names — see VUE3-MIGRATION) ── */
.play-sidebar-enter-active,
.play-sidebar-leave-active {
  transition: opacity var(--dur) var(--ease-out);
}

.play-sidebar-enter-active .play-sidebar__panel,
.play-sidebar-leave-active .play-sidebar__panel {
  transition: transform var(--dur) var(--ease-out);
}

.play-sidebar-enter,
.play-sidebar-leave-to { opacity: 0; }

.play-sidebar--right.play-sidebar-enter .play-sidebar__panel,
.play-sidebar--right.play-sidebar-leave-to .play-sidebar__panel { transform: translateX(100%); }

.play-sidebar--left.play-sidebar-enter .play-sidebar__panel,
.play-sidebar--left.play-sidebar-leave-to .play-sidebar__panel { transform: translateX(-100%); }

@media (prefers-reduced-motion: reduce) {
  .play-sidebar-enter-active,
  .play-sidebar-leave-active,
  .play-sidebar-enter-active .play-sidebar__panel,
  .play-sidebar-leave-active .play-sidebar__panel,
  .play-sidebar__close { transition: none; }

  .play-sidebar--right.play-sidebar-enter .play-sidebar__panel,
  .play-sidebar--right.play-sidebar-leave-to .play-sidebar__panel,
  .play-sidebar--left.play-sidebar-enter .play-sidebar__panel,
  .play-sidebar--left.play-sidebar-leave-to .play-sidebar__panel { transform: none; }
}
</style>
